import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './tooltip.style.scss';

interface TooltipProps {
  /** Content to display inside the tooltip */
  content: string;
  /** Children element that triggers the tooltip */
  children: React.ReactNode;
  /** Position of the tooltip relative to the trigger element */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Additional CSS classes */
  className?: string;
  /** Inline styles for the tooltip wrapper */
  style?: React.CSSProperties;
}


export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  className = '',
  style
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const updateTooltipPosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let x = 0;
    let y = 0;

    switch (position) {
      case 'top':
        x = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        y = triggerRect.top - tooltipRect.height - 8;
        break;
      case 'bottom':
        x = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        y = triggerRect.bottom + 8;
        break;
      case 'left':
        x = triggerRect.left - tooltipRect.width - 8;
        y = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        break;
      case 'right':
        x = triggerRect.right + 8;
        y = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        break;
      default:
        x = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        y = triggerRect.top - tooltipRect.height - 8;
    }

    // keep the tooltip within the viewport horizontally
    const gutter = 8;
    const maxX = window.innerWidth - tooltipRect.width - gutter;
    if (x > maxX) x = maxX;
    if (x < gutter) x = gutter;

    setTooltipPosition({ x, y });
  };

  // Position once it becomes visible (before paint, so there's no flash).
  useLayoutEffect(() => {
    if (isVisible) {
      updateTooltipPosition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, position, content]);

  // Keep it anchored to the icon while scrolling / resizing.
  useEffect(() => {
    if (!isVisible) return;
    const reposition = () => updateTooltipPosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  return (
    <div
      className={`tooltip-wrapper ${className}`}
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={style}
    >
      {children}

      {/*
        Rendered in a portal on <body> so the fixed-position coordinates are
        relative to the viewport — unaffected by ancestors that establish a
        containing block via `transform` / `overflow: hidden`.
      */}
      {createPortal(
        <div
          ref={tooltipRef}
          className={`tooltip tooltip--${position} ${isVisible ? 'tooltip--visible' : ''}`}
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            zIndex: 9999
          }}
        >
          <div className="tooltip__content">
            {content}
          </div>
          <div className="tooltip__arrow" />
        </div>,
        document.body
      )}
    </div>
  );
};
